import React, { useEffect, useId } from 'react';
import { connect } from 'react-redux';
import { usersActions } from 'users/actions';
import OrgSelectorStyled from './styled';

/**
 * Organization data filter for the admin dashboard.
 *
 * Rendered only for unrestricted admins (Django staff / superusers). Course
 * staff are scoped server-side to their own org and never see this control.
 * Selecting an org writes it to redux + localStorage; the axios interceptor
 * then attaches it to every admin-panel request. The App remounts the routed
 * content on change so all data refetches for the chosen org.
 */
const OrgSelector = ({
  isStaff,
  isSuperuser,
  organizations,
  organizationsError,
  selectedOrg,
  dispatch,
}) => {
  const canSelectOrg = isStaff || isSuperuser;
  // Unique per instance so the desktop and mobile sidebars don't collide on a
  // shared DOM id (label/select pairing stays valid in each).
  const fieldId = useId();

  useEffect(() => {
    if (canSelectOrg && organizations.length === 0) {
      dispatch(usersActions.fetchOrganizations());
    }
  }, [canSelectOrg, organizations.length, dispatch]);

  useEffect(() => {
    // Drop a persisted org that no longer exists / is inactive so the control
    // and the value sent to the backend stay in sync.
    if (
      canSelectOrg
      && selectedOrg
      && organizations.length
      && !organizations.some((org) => org.short_name === selectedOrg)
    ) {
      dispatch(usersActions.setSelectedOrg(''));
    }
  }, [canSelectOrg, selectedOrg, organizations, dispatch]);

  if (!canSelectOrg) {
    return null;
  }

  return (
    <OrgSelectorStyled>
      <label htmlFor={fieldId}>
        <span className="field-label">Organization</span>
        <select
          id={fieldId}
          value={selectedOrg || ''}
          onChange={(event) => dispatch(usersActions.setSelectedOrg(event.target.value))}
          aria-label="Filter dashboard by organization"
        >
          <option value="">All organizations</option>
          {organizations.map((org) => (
            <option key={org.short_name} value={org.short_name}>
              {org.name ? `${org.short_name} (${org.name})` : org.short_name}
            </option>
          ))}
        </select>
      </label>
      {organizationsError && (
        <button
          type="button"
          className="org-retry"
          onClick={() => dispatch(usersActions.fetchOrganizations())}
        >
          Failed to load. Retry
        </button>
      )}
    </OrgSelectorStyled>
  );
};

function mapStateToProps(state) {
  return {
    isStaff: state.LOGGEDINUSER.isStaff,
    isSuperuser: state.LOGGEDINUSER.isSuperuser,
    organizations: state.LOGGEDINUSER.organizations || [],
    organizationsError: state.LOGGEDINUSER.organizationsError || false,
    selectedOrg: state.LOGGEDINUSER.selectedOrg || '',
  };
}

export default connect(mapStateToProps)(OrgSelector);
